import AuthMan from './AuthMan';
import { config, ConnectionSecurityMode, Protocol, SavePasswordMode } from './AuthConfig';
import { Logger } from './Logger';
import { AotCategoryType, logConstants } from '../utils/Constants';
import { StringMap } from '../utils/TypeDefinitions';
import {
	HTTPFetchResponse,
	HeadersJson,
	PrimaryTokenTableEntry,
	SsoCredentialType,
	UserNameDomainCredentials,
} from './dsAuth/data/AuthTypes';
import { OIDCTokensTableEntry } from './oidc/TypeDefinitions';
import { ProtectionMode } from './dsAuth/data/AuthTypes';
import {
	AMLErrorCode,
	AuthStorageErrorCodes,
	AuthStorageInitError,
	GatewaySessionError,
} from './AuthErrors';
import GatewayService from './gateway/GatewayService';
import { NetworkLocation, StoreConfiguration } from '../Common/TypeDefinitions';
import {
	AuthenticatedFetchErrorJSON,
	AuthenticatedFetchErrors,
} from '../Common/Definitions';
import { Platform } from './AuthConstants';
import { AppName } from '../utils/Constants';
import { AuthBridge } from '../Common/AuthBridge';
import NetworkLocationHandler from './beacon/NetworkLocationHandler';

export interface GetCachedPrimaryTokenEntryParams {
	authDomain: string;
	referenceUri: string;
}

export type primaryTokenEntryParam = Omit<PrimaryTokenTableEntry, 'tokenExpiryDate'> & {
	tokenExpiryDate: string;
};
export interface StorePrimaryTokenEntryParams {
	primaryTokenEntry: primaryTokenEntryParam;
	encryptToken: boolean;
}
export interface GetCachedOIDCTokensEntryParams {
	authDomain: string;
	referenceUri: string;
}

export interface StoreOIDCTokensEntryParams {
	oidcTokensEntry: OIDCTokensTableEntry;
	encryptTokens: boolean;
}

export interface GetSessionCookieParams {
	url: string;
}

export interface GetTranslatedUrlParams {
	url: string;
}

export interface GetCachedOIDCTokensEntryCompleteParams extends OIDCTokensTableEntry {
	revocationEndpoint?: string;
}

export interface GetSecondaryTokenForUrlParams {
	rawParams: string;
	body: string;
	headers: string;
	id: string;
}

export interface GatewayAuthenticatedFetchParams {
	method: string;
	url: string;
	headers: HeadersJson;
	body: string;
	referenceUri: string;
	gatewayUrl: string;
	allowCredUi: boolean;
	id?: string;
}

export interface AuthFetchWrapperParams {
	method: string;
	url: string;
	headers: HeadersJson;
	body: string;
	referenceUri: string;
	allowCredUi: boolean;
	id?: string;
}

export enum UrlTranslationType {
	GatewayToStorefront = 'GatewayToStorefront',
	StorefrontToGateway = 'StorefrontToGateway',
}

type Writable<T> = {
	-readonly [K in keyof T]: T[K];
};
declare global {
	interface Window extends AuthBridge {
		ctxs_exit_webview: Function;
		authStart: Function;
		SendReceiveWithToken: Function;
		getSecondaryTokenForUrl(params: string): Promise<void>;
		clearStorage: Function;
		deleteCachedTokens: Function;
		handleLogOffForStore: Function;
		ctxsNative: NativeInterface;
		isLoggedOnToAMLService: Function;
		AMLInit(initData: StringMap): Promise<void>;
		setStoreConfiguration: Function;
		networkAvailabilityChanged(params: string): Promise<void>;
		putStoreConfiguration: Function;
		deleteStoreConfiguration: Function;
		setupAML(initData: StringMap): Promise<void>;
		fetchComplete: Function;
		getCachedPrimaryTokenEntry(id: string, params: string): Promise<void>;
		getCachedOIDCTokensEntry(id: string, params: string): Promise<void>;
		storePrimaryTokenEntry(id: string, params: string): Promise<void>;
		storeOIDCTokensEntry(id: string, params: string): Promise<void>;
		getSessionCookie(id: string, params: string): void;
		getTranslatedUrl(id: string, params: string): void;
		translateURL(
			url: string,
			referenceUri: string,
			translationType: UrlTranslationType
		): Promise<string>;
		getNetworkLocation(params: string): Promise<NetworkLocation>;
		getSsoCredentialsComplete: Function;

		/*
		 * Gateway functions exposed for accountConfig, these are not bridge functions
		 * In order to remove dependency of accountConfig on auth
		 */
		gatewayAuthenticatedFetch(params: string): Promise<HTTPFetchResponse>;
		getGatewayRoamingAccountsResponse(url: string): Promise<HTTPFetchResponse>;
		setReferenceUriToGatewaySession(
			sessionUrl: URL,
			referenceUri: string
		): Promise<boolean>;
		authFetch(
			method: string,
			url: string,
			headers: HeadersJson,
			body: string,
			referenceUri: string,
			allowCredUi: boolean,
			id?: string
		): Promise<HTTPFetchResponse>;
		getStoreConfiguration(): Promise<StoreConfiguration>;
		evaluateNetworkLocationAndStoreConfiguration(params: string): Promise<void>;
		getNetworkLocationForURL(params: string): Promise<NetworkLocation>;
		getCredentialsComplete: Function;
		isIWAEnabledComplete: Function;
	}
}

export interface AuthmanLiteReadyParams {
	error?: {
		errorCode: number;
		errorMessage: string;
	};
}
export interface OpenLoginUrlParams {
	startUrl: string;
	referenceUri: string;
	webviewType: string;
	cookies: string;
	transactionId: string;
	id: string;
	exitMode: 'jsCallback' | 'redirectUri';
	exitRedirectUri: string;
	isGatewayStore: boolean;
}
export interface CryptoParams {
	referenceUri: string;
	token: string;
	protectionMode?: ProtectionMode;
}

export interface SetLoggedoffCompleteParams {
	[referenceuri: string]: {
		weblogoffUrl?: string;
	};
}

export interface IsIWAEnabledParams {
	storeUrl: string;
}

export interface IsIWAEnabledResponse {
	response: boolean;
}

export interface GetSsoCredentialsParams {
	credentialsType: SsoCredentialType;
}

export interface GetSsoCredentialsResponse {
	userNameDomain?: UserNameDomainCredentials;
	pin?: string;
}

export interface WriteTraceParams {
	level?: string;
	aotCategory?: AotCategoryType;
	trace: string;
}

interface NativeInterface {
	fetch: Function;
	SetResponseWithToken: Function;
	SetResponse: Function;
	getSecondaryTokenForUrlComplete(responseObjString: string): void;
	/** @deprecated use openLoginUrl */
	openChildTab(
		startUrl: string,
		referenceUri: string,
		webviewType: string,
		cookies: string
	): Function;
	openLoginUrl(params: string): void;
	dispatchEventToNative(params: string): void;
	SetLoggedOnState: Function;
	SetLoggedOffComplete: Function;
	Encrypt: Function;
	Decrypt(referenceUri: string, token: string): string;
	WriteAMLTrace(params: string): void;
	AuthmanLiteReady(errorObjString?: string): void;
	fetchWithCallback: Function;
	RefreshTokenFlowTriggered: Function;
	getCachedPrimaryTokenEntryComplete(id: string, tokenObjectString: string): void;
	getCachedOIDCTokensEntryComplete(id: string, tokenObjectString: string): void;
	getSessionCookieComplete(id: string, params: string): void;
	getTranslatedUrlComplete(id: string, params: string): void;
	EncryptV2(params: string): string;
	DecryptV2(params: string): string;
	getCredentials: Function;
	isIWAEnabled(jsonStr: string): void;
	getSsoCredentials(params: string): string;
}

let authMan: AuthMan;

/// <summary>
/// pass web view response from native to AML SDK
/// </summary>
/// <param name="response">web view response</param>
/// <param name="referenceUri">discovery url</param>
/// <param name="webViewType">oidcwebview or authwebview</param>

// eslint-disable-next-line @typescript-eslint/naming-convention
window.ctxs_exit_webview = async function (
	response: string,
	referenceUri: string,
	webViewType: string
): Promise<void> {
	// eslint-disable-line @typescript-eslint/naming-convention
	Logger.getInstance().write(
		logConstants.INFO,
		'exit webview called for referenceUri ' +
			referenceUri +
			' and webviewType ' +
			webViewType
	);
	await authMan.ctxsExitWebview(response, referenceUri);
};

/// <summary>
/// make the url request and return response to native by calling ctxsNative.SetResponse
/// </summary>
/// <param name="rawParams">JSON string containing request parameters
/// example : "{\"authType\":\"DSAuth\",\"method\":\"POST\",\"url\":\"https://myworkprod0.cloud.com/Citrix/Store/resources/v2/NThkMGY3YWQtM2ZhNi00MDZhLWFmZmYtN2VhZGMzY2IwYjNkLkFkb2JlIFJlYWRlcg--/launch/ica\",\"referenceUri\":\"https://myworkprod0.cloud.com/Citrix/Store/discovery\",\"allowCredUi\":\"true\"}"
/// </param>
/// <param name="body">request body</param>
/// <param name="headers">request headers</param>
/// <param name="id">request id</param>
window.authStart = async function (
	rawParams: string,
	body: string,
	headers: string,
	id: string
): Promise<void> {
	await authMan.startUrlRequest(rawParams, body, headers, id);
};

/// <summary>
/// make the url request and return response along with token to native by calling ctxsNative.SetResponseWithToken
/// </summary>
/// <param name="rawParams">JSON string containing request parameters
/// example : "{\"authType\":\"DSAuth\",\"method\":\"POST\",\"url\":\"https://myworkprod0.cloud.com/Citrix/Store/resources/v2/NThkMGY3YWQtM2ZhNi00MDZhLWFmZmYtN2VhZGMzY2IwYjNkLkFkb2JlIFJlYWRlcg--/launch/ica\",\"referenceUri\":\"https://myworkprod0.cloud.com/Citrix/Store/discovery\",\"allowCredUi\":\"true\"}"
/// </param>
/// <param name="body">request body</param>
/// <param name="headers">request headers</param>
/// <param name="id">request id</param>
window.SendReceiveWithToken = async function (
	rawParams: string,
	body: string,
	headers: string,
	id: string
): Promise<void> {
	await authMan.startUrlRequest(rawParams, body, headers, id);
};

/// <summary>
/// fetch the secondary token for the url and return encrypted token to native by calling ctxsNative.getSecondaryTokenForUrlComplete
/// </summary>
/// <param name="params">JSON string containing request parameters, body, headers and id

window.getSecondaryTokenForUrl = async function (params: string) {
	const paramsJson = JSON.parse(params) as GetSecondaryTokenForUrlParams;
	const { rawParams, body, headers, id } = paramsJson;
	await authMan.getSecondaryTokenForUrl(rawParams, body, headers, id);
};

/// <summary>
/// delete all tokens and cookies from the db. this method is called by native on logoff
/// </summary>
window.clearStorage = async function (id: string): Promise<void> {
	await authMan.handleLogoff(id);
};

/// <summary>
/// delete all token tables from the db.
/// </summary>
window.deleteCachedTokens = async function (): Promise<void> {
	await authMan.clearCachedTokens();
};

/// <summary>
/// For a given store delete all tokens and cookies from the db. this method is called by native on user inactivity timeout.
/// </summary>
window.handleLogOffForStore = async function (
	id: string,
	referenceUri: string
): Promise<void> {
	await authMan.handleLogOffForStore(id, referenceUri);
};

/// <summary>
/// check if AML has a primary token for given service url
/// </summary>
window.isLoggedOnToAMLService = async function (
	id: string,
	serviceURL: string
): Promise<void> {
	await authMan.isLoggedOnToAMLService(id, serviceURL);
};

/// <summary>
/// initialise AML with params given by native
/// </summary>
/// <param name="amlInitData"> AML initial configuration data </param>
window.AMLInit = async function (initData: StringMap): Promise<void> {
	Logger.getInstance().write(
		logConstants.INFO,
		`amlInitData: ${JSON.stringify(initData)}`
	);

	if (initData.primaryTokenLifetime) {
		config.primaryTokenLifetime = initData.primaryTokenLifetime;
	}
	if (initData.secondaryTokenLifetime) {
		config.secondaryTokenLifetime = initData.secondaryTokenLifetime;
	}
	if (initData.primaryTokenLifetimeOnPrem) {
		config.primaryTokenLifetimeOnPrem = initData.primaryTokenLifetimeOnPrem;
	}
	if (initData.secondaryTokenLifetimeOnPrem) {
		config.secondaryTokenLifetimeOnPrem = initData.secondaryTokenLifetimeOnPrem;
	}
	if (initData.nativeLoggingEnabled) {
		config.nativeLoggingEnabled = JSON.parse(initData.nativeLoggingEnabled.toLowerCase());
	}
	if (initData.uiInactivityTimeoutInSeconds) {
		config.uiInactivityTimeOutForOnPremInSeconds = Number(
			initData.uiInactivityTimeoutInSeconds
		);
	}
	if (initData.longLivedTokenSupport) {
		config.longLivedTokenSupport = JSON.parse(
			initData.longLivedTokenSupport.toLowerCase()
		);
	}
	if (initData.platform) {
		const platform = initData.platform.toUpperCase();
		if (platform in Platform) {
			config.platform = Platform[platform as keyof typeof Platform];
		}
	}
	if (initData.hostApp) {
		const hostApp = initData.hostApp.toUpperCase();
		if (hostApp in AppName) {
			config.hostApp = AppName[hostApp as keyof typeof AppName];
		}
	} else {
		config.hostApp = AppName.WORKSPACE;
	}
	if (initData.saveTokens) {
		const saveTokens = JSON.parse(initData.saveTokens.toLowerCase());
		if (saveTokens === false) {
			config.longLivedTokenSupport = false;
			await authMan.destroyTokensAndClearTablesExceptCredentials(
				undefined,
				'ProtectData'
			);
		}
		config.saveTokens = saveTokens;
	}
	if (initData.compressedGroupsEnabled) {
		config.compressedGroupsEnabled = JSON.parse(
			initData.compressedGroupsEnabled.toLowerCase()
		);
	}
	if (initData.locale) {
		config.locale = initData.locale;
	}
	if (initData.isUnifiedAuthenticationEnabled) {
		config.isUnifiedAuthenticationEnabled = JSON.parse(
			initData.isUnifiedAuthenticationEnabled.toLowerCase()
		);
		if (!config.isUnifiedAuthenticationEnabled) {
			await authMan.clearInvalidCredentialsTableEntries();
		}
	}
	if (initData.isDSAuthResiliencyEnabled) {
		config.isDSAuthResiliencyEnabled = JSON.parse(
			initData.isDSAuthResiliencyEnabled.toLowerCase()
		);
	}
	if (initData.isSimplifyBeaconLogicEnabled) {
		config.isSimplifyBeaconLogicEnabled = JSON.parse(
			initData.isSimplifyBeaconLogicEnabled.toLowerCase()
		);
	}
	if (initData.rememberUsername) {
		config.rememberUsername = JSON.parse(initData.rememberUsername.toLowerCase());
		if (!config.rememberUsername && config.isUnifiedAuthenticationEnabled) {
			await authMan.clearInvalidCredentialsTableEntries();
		}
	}
	if (initData.savePasswordMode) {
		const inputSavePasswordMode = initData.savePasswordMode.toLowerCase();
		if (inputSavePasswordMode === SavePasswordMode.Never.toLowerCase()) {
			config.savePasswordMode = SavePasswordMode.Never;
		} else if (inputSavePasswordMode === SavePasswordMode.SecureOnly.toLowerCase()) {
			config.savePasswordMode = SavePasswordMode.SecureOnly;
		}
		if (
			config.isUnifiedAuthenticationEnabled &&
			config.savePasswordMode !== SavePasswordMode.Always
		) {
			await authMan.clearInvalidCredentialsTableEntries();
		}
	}
	if (initData.protocolOrder) {
		const protocolOrder = initData.protocolOrder
			.split(',')
			.map((protocol) => protocol.trim().toLowerCase() as Protocol)
			.filter((protocol) => protocol);
		if (protocolOrder?.length) config.protocolOrder = protocolOrder;
	}
	if (initData.CredentialInsertionEnabled) {
		config.CredentialInsertionEnabled = JSON.parse(
			initData.CredentialInsertionEnabled.toLowerCase()
		);
	}
	if (initData.connectionSecurityMode) {
		const connectionSecurityMode = initData.connectionSecurityMode.toLowerCase();
		config.connectionSecurityMode =
			connectionSecurityMode === ConnectionSecurityMode.Any.toLowerCase()
				? ConnectionSecurityMode.Any
				: ConnectionSecurityMode.SecureOnly;
	}
	if (initData.httpBasicAuthEnabled) {
		config.httpBasicAuthEnabled = JSON.parse(initData.httpBasicAuthEnabled.toLowerCase());
	}
};

/// <summary>
/// Resests the store configurations with the current input
/// </summary>
/// <param name="configuration">store specific configurations</param>
/// example config :
/// {
/// 	"https://akshaytest.cloudburrito.com/Citrix/Store/discovery" : {
/// 		"tokenServiceURL" : "https://accounts-dsauthweb.cloudburrito.com/tAaJp1HlzUyjbu3Bm2-eFg/token"
/// 	},
/// 	"https://myworkprod0.cloud.com/Citrix/Store/discovery": {
/// 		"tokenServiceURL" : "https://accounts-dsauthweb.cloud.com/y5xIpTnN9EKaEyayBvYg-g/token",
///			"authDomain": "y5xIpTnN9EKaEyayBvYg-g",
///			"oidcConfiguration" : "{"oidc_discovery_endpoint":"https://accounts.cloudburrito.com/core/.well-known/openid-configuration","oidc_clients":{"android":[{"name":"android-apps-athena-client","id":"F96sxaQFnp6STEnZxDRT0A=="}],
///					"iOS":[{"name":"ios-apps-athena-client","id":"HzJ8PGTUrAScnrraa251cA=="}],"mac":[{"name":"mac-aml-app-athena-client","id":"wDXC-yCz_Lohi32nfXQ-1w=="}],"windows":[{"name":"windows-aml-app-athena-client","id":"VUjM0IWrDyQto94cNr-bRg=="}]}}"
/// 	}
/// }
/// Note: tokenServiceURL is compulsary. Rest are optional
window.setStoreConfiguration = async function (configuration: string): Promise<void> {
	Logger.getInstance().write(logConstants.INFO, 'StoreConfiguration:  ' + configuration);
	await authMan.setStoreConfiguration(configuration, true, true);
};

/// <summary>
/// setStoreConfiguration also removes the stores that are not part of current input.
/// putStoreConfiguration in contrast with above, either adds or modifies and ensures the older ones are not removed
/// This helps the selfservice to not maintain all the store configurations and relies on JS module to do it
/// To remove the store use deleteStoreConfiguration
/// </summary>
/// <param name="configuration">store specific configurations</param>
/// example config :
/// {
/// 	"https://akshaytest.cloudburrito.com/Citrix/Store/discovery" : {
/// 		"tokenServiceURL" : "https://accounts-dsauthweb.cloudburrito.com/tAaJp1HlzUyjbu3Bm2-eFg/token".
/// 		"authDomain": "tAaJp1HlzUyjbu3Bm2-eFg",
/// 	}
/// }
///Note: Either putStoreConfiguration or setStoreConfiguration has to be called before making any authenticated requests.
///      Without which selfservice will not make calls
window.putStoreConfiguration = async function (configuration: string): Promise<void> {
	Logger.getInstance().write(logConstants.INFO, 'StoreConfiguration: ' + configuration);
	await authMan.setStoreConfiguration(configuration, true /*retainOldStores*/);
};

/// <summary>
/// deleteStoreConfiguration remove a particular config from database
/// </summary>
/// <param name="refUri"> referenceUri </param>
window.deleteStoreConfiguration = async function (refUri: string): Promise<void> {
	Logger.getInstance().write(logConstants.INFO, 'RefUri: ' + refUri);
	await authMan.deleteStoreConfiguration(refUri);
};

/// <summary>
/// getCachedPrimaryTokenEntry returns encrypted cached primarytoken token which is present in the database or null
/// </summary>
/// <param name="id"> used to send response in callback </param>
/// <param name="params"> JSON stringifed of the GetCachedPrimaryTokenEntryParams interface which has authdomain and referenceUri </param>
window.getCachedPrimaryTokenEntry = async function (
	id: string,
	params: string
): Promise<void> {
	const { authDomain, referenceUri } = JSON.parse(
		params
	) as GetCachedPrimaryTokenEntryParams;
	Logger.getInstance().write(
		logConstants.INFO,
		`getCachedPrimaryTokenEntry:  id: ${id}, authdomain: ${authDomain}, refUri: ${referenceUri}`
	);
	await authMan.getCachedPrimaryTokenEntry(id, authDomain, referenceUri, false);
};

/// <summary>
/// storePrimaryTokenEntry stores the primary token table entry into the DB
/// </summary>
/// <param name="id"> id for logging </param>
/// <param name="params"> Stringifed JSON of the StorePrimaryTokenEntryParams interface which has primaryTokenEntry object and encryptToken </param>
window.storePrimaryTokenEntry = async function (
	id: string,
	params: string
): Promise<void> {
	const { primaryTokenEntry, encryptToken } = JSON.parse(
		params
	) as StorePrimaryTokenEntryParams;
	if (!primaryTokenEntry) {
		Logger.getInstance().write(
			logConstants.INFO,
			`storePrimaryTokenEntry:  id: ${id}, token object is null`
		);
		return;
	}
	Logger.getInstance().write(
		logConstants.INFO,
		`storePrimaryTokenEntry:  id: ${id}, referenceUri: ${primaryTokenEntry.referenceUri}, authDomain: ${primaryTokenEntry.authDomain}`
	);
	const tokenObj = {
		...primaryTokenEntry,
	} as unknown as Writable<PrimaryTokenTableEntry>;
	tokenObj.tokenExpiryDate = new Date(tokenObj.tokenExpiryDate);
	await authMan.storePrimaryTokenEntry(tokenObj, encryptToken, 'native');
};

/// <summary>
/// getCachedOIDCTokensEntry returns encrypted cached oidc tokens which are present in the database or null
/// </summary>
/// <param name="id"> will be used to send response callback </param>
/// <param name="params"> stringifed JSON of the GetCachedOIDCTokensEntryParams interface which has authdomain and referenceUri </param>
window.getCachedOIDCTokensEntry = async function (id, paramsStr) {
	const { authDomain, referenceUri } = JSON.parse(
		paramsStr
	) as GetCachedOIDCTokensEntryParams;
	Logger.getInstance().write(
		logConstants.INFO,
		`getCachedOIDCTokensEntry:  id: ${id}, authdomain: ${authDomain}, refUri: ${referenceUri}`
	);
	await authMan.getCachedOIDCTokensEntry(id, authDomain, referenceUri, false);
};

/// <summary>
/// storeOIDCTokensEntry stores the oidc tokens table entry into the DB
/// </summary>
/// <param name="id"> id for logging </param>
/// <param name="params"> Stringifed JSON of the StoreOIDCTokensEntryParams interface which has oidcTokensEntry object and encryptToken. If encryptToken is not passed then it'll set it as true by default </param>
window.storeOIDCTokensEntry = async function (id: string, params: string) {
	const { oidcTokensEntry, encryptTokens = true } = JSON.parse(
		params
	) as StoreOIDCTokensEntryParams;
	if (!oidcTokensEntry) {
		Logger.getInstance().write(
			logConstants.INFO,
			`storeOIDCTokensEntry:  id: ${id}, token object is null`
		);
		return;
	}
	const { referenceUri } = oidcTokensEntry;
	Logger.getInstance().write(
		logConstants.INFO,
		`storeOIDCTokensEntry:  id: ${id}, refUri: ${referenceUri}`
	);
	await authMan.storeOIDCTokensEntry(id, oidcTokensEntry, encryptTokens);
};

/// <summary>
/// getSessionCookie fetches the gateway session cookie (NSC_AAAC) for the given url in params
/// </summary>
/// <param name="params"> Stringifed JSON of the url </param>
window.getSessionCookie = function (id: string, params: string): void {
	const { url } = JSON.parse(params) as GetSessionCookieParams;

	Logger.getInstance().write(
		logConstants.INFO,
		`getSessionCookie: id: ${id}, fetching session cookie for the url: ${url}`
	);
	authMan.getGatewaySessionCookie(id, url);
};

/// <summary>
/// getTranslatedUrl sends the translated url for the given url in params, if the session is present
/// </summary>
/// <param name="params"> Stringifed JSON of the url </param>
window.getTranslatedUrl = function (id: string, params: string): void {
	const { url } = JSON.parse(params) as GetTranslatedUrlParams;

	Logger.getInstance().write(
		logConstants.INFO,
		`getTranslatedUrl: id: ${id}, translating the url: ${url}`
	);
	authMan.getGatewayTranslatedUrl(id, url);
};

/// <summary>
/// translate returns the translated url based on the translation type
/// </summary>
/// <param name="params"> Stringifed JSON of the url, referenceUri, translationType </param>
window.translateURL = function (
	url: string,
	referenceUri: string,
	translationType: UrlTranslationType
): Promise<string> {
	return authMan.translateURL(url, referenceUri, translationType);
};

/// <summary>
/// initialize Auth man lite and storage.
/// </summary>
window.setupAML = async function (initData: StringMap): Promise<void> {
	try {
		authMan = await AuthMan.createInstance();
		await window.AMLInit(initData);
	} catch (e) {
		let errorObj: AuthmanLiteReadyParams;
		if (e instanceof AuthStorageInitError) {
			let code;
			switch (e.errorCode) {
				case AuthStorageErrorCodes.AML_DB_OPEN_FAILURE:
				case AuthStorageErrorCodes.AML_DB_OPEN_FAILURE_DELETED_DB_SUCCESSFULLY:
					code = AMLErrorCode.AML_REQUEST_WEBVIEW_RESTART;
					break;
				default:
					code = AMLErrorCode.AML_INIT_GENERAL_FAILURE;
					break;
			}
			errorObj = { error: { errorCode: code, errorMessage: e.message } };
		} else {
			errorObj = {
				error: {
					errorCode: AMLErrorCode.AML_INIT_GENERAL_FAILURE,
					errorMessage: e.message,
				},
			};
		}
		AuthMan.ready(errorObj);
		return;
	}
	AuthMan.ready({});
};

window.gatewayAuthenticatedFetch = async function (
	params: string
): Promise<HTTPFetchResponse> {
	const { method, url, headers, body, referenceUri, gatewayUrl, allowCredUi, id } =
		JSON.parse(params) as GatewayAuthenticatedFetchParams;

	return await GatewayService.getInstance().fetch(
		method,
		url,
		headers,
		body,
		referenceUri,
		allowCredUi,
		id,
		gatewayUrl
	);
};

window.getGatewayRoamingAccountsResponse = async function (
	url: string
): Promise<HTTPFetchResponse> {
	return await GatewayService.getInstance().getRoamingAccountsResponse(url);
};

window.setReferenceUriToGatewaySession = async function (
	sessionUrl: URL,
	referenceUri: string
): Promise<boolean> {
	return Promise.resolve(
		GatewayService.getInstance().setReferenceUriToGatewaySession(sessionUrl, referenceUri)
	);
};

window.authFetch = async function (
	method: string,
	url: string,
	headers: HeadersJson,
	body: string,
	referenceUri: string,
	allowCredUi: boolean,
	id?: string
): Promise<HTTPFetchResponse> {
	const fetch = await AuthMan.getInstance().getFetchInstanceFromStoreConfiguration(
		referenceUri
	);
	return await fetch(method, url, headers, body, referenceUri, allowCredUi, id);
};

/**
 * Wrapper function for authenticatedFetch with additional error handling without which the error object sent from auth cannot
 * be processed in accountConfig. Hence, convert it to AuthenticatedFetchErrorJSON so that it can be processed in accountConfig
 */
window.gatewayAuthenticatedFetchWrapper = async function (
	params: string
): Promise<HTTPFetchResponse | AuthenticatedFetchErrorJSON> {
	try {
		return await window.gatewayAuthenticatedFetch(params);
	} catch (e) {
		const message: string = e.message;
		let type: AuthenticatedFetchErrors;
		if (e instanceof GatewaySessionError) {
			type = AuthenticatedFetchErrors.GatewaySessionError;
		} else {
			type = AuthenticatedFetchErrors.AuthenticationError;
		}

		const errorObj: AuthenticatedFetchErrorJSON = {
			error: {
				message,
				type,
			},
		};
		return errorObj;
	}
};

window.authFetchWrapper = async function (
	params: string
): Promise<HTTPFetchResponse | AuthenticatedFetchErrorJSON> {
	try {
		const { method, url, headers, body, referenceUri, allowCredUi, id } = JSON.parse(
			params
		) as AuthFetchWrapperParams;
		return await window.authFetch(
			method,
			url,
			headers,
			body,
			referenceUri,
			allowCredUi,
			id
		);
	} catch (e) {
		const message: string = e.message;
		let type: AuthenticatedFetchErrors;
		if (e instanceof GatewaySessionError) {
			type = AuthenticatedFetchErrors.GatewaySessionError;
		} else {
			type = AuthenticatedFetchErrors.AuthenticationError;
		}

		const errorObj: AuthenticatedFetchErrorJSON = {
			error: {
				message,
				type,
			},
		};
		return errorObj;
	}
};

window.getStoreConfiguration = async function (): Promise<StoreConfiguration> {
	return Promise.resolve(AuthMan.getInstance().getStoreConfiguration());
};

window.getNetworkLocation = async function (params: string): Promise<NetworkLocation> {
	const { internalBeacon, discoveryURL } = JSON.parse(params);
	return await NetworkLocationHandler.getInstance().GetNetworkLocationForStore(
		discoveryURL,
		internalBeacon
	);
};

/**
 * Evaluate network location and store the meta data against the discovery URL in the store configuration
 * This function is not exposed to native and is called from accountConfig via AuthBridge
 * @param params JSON string containing discoveryURL, internalBeacon and storeType
 */
window.evaluateNetworkLocationAndStoreConfiguration = async function (
	params: string
): Promise<void> {
	await AuthMan.getInstance().evaluateNetworkLocationAndStoreConfiguration(params);
};

window.networkAvailabilityChanged = async function (params: string): Promise<void> {
	await AuthMan.getInstance().reevaluateBeaconsOnNetworkChange(params);
};

window.getNetworkLocationForURL = async function (
	params: string
): Promise<NetworkLocation> {
	const { url } = JSON.parse(params);
	return await NetworkLocationHandler.getInstance().GetNetworkLocationForURL(url);
};
